import type { Uuid } from '@sibe/utilities/common';
import type { HsPerspectiveCamera } from '../../engine-new/objects/cameras/perspective-camera.class';
import type { HsObject3D } from '../../engine-new/objects/common/object-3d.class';
import type { HsGraphics } from '../../graphic-render-new/graphics.class';
import type { HsGraphicLines } from '../../graphic-render-new/objects/graphic-lines.class';
import type { HsGraphicObject } from '../../graphic-render-new/objects/graphic-object.class';
import type { HsGraphicQueue } from '../../graphic-render-new/objects/graphic-queue.class';
import { HsHypersceneConstants } from '../../math-new/constants/hyperscene-constants.global';
import { HsColor } from '../../math-new/objects/color.class';
import { HsMatrix3 } from '../../math-new/objects/matrix3.class';
import { HsVector3 } from '../../math-new/objects/vector3.class';
import { HsBox } from '../../math-new/shapes/box.class';
import { HsPlane } from '../../math-new/shapes/plane.class';
import type { HsHypersceneTypes } from '../../math-new/types/hyperscene-types.type';
import { HsBoxHelper } from '../helpers/box-helper.class';
import type { HsArrowTool } from './arrow-tool.class';
import { HsClippingArrow } from './clipping-arrow.class';
import { HsMoveTool } from './move-tool.class';
import { HsToolActionType } from './tool-action-type.class';
import { HsToolAction } from './tool-action.class';

const LINES_VISIBLE_OFFSET: number = 0.01;

export class HsClippingBox {
  public limitMin: number = 10;
  public limitMax: number = 100;

  private readonly planeColor: HsColor = new HsColor(0.7, 0.7, 0.7);

  private readonly axes: HsClippingArrow[] = [];

  private transformAxis: HsMoveTool | null = null;

  private lines: HsGraphicLines | null = null;

  private movingObjectUuid: Uuid | null = null;

  private cameraWasUpdated: boolean = false;

  private needUpdateAfterMoving: boolean = false;

  private readonly movedAmount: HsVector3 = HsVector3.makeZeroVector();

  static readonly #matrix3Temp1: HsMatrix3 = HsMatrix3.makeIdentityMatrix();

  constructor(
    private readonly graphics: HsGraphics,
    private readonly queue: HsGraphicQueue,
    private readonly camera: HsPerspectiveCamera
  ) {}

  public createPlanes(position: HsHypersceneTypes.Array3, size: number): void {
    this.limitMin = size;
    this.addClippingPlane(
      position,
      [1, 0, 0],
      [0, 0, 1],
      new HsToolAction(HsToolActionType.Move, [0], [1, 2], 0, -this.limitMax),
      [1, 0, 0]
    );
    this.addClippingPlane(
      position,
      [-1, 0, 0],
      [0, 0, -1],
      new HsToolAction(HsToolActionType.Move, [0], [1, 2], this.limitMax, 0),
      [1, 0, 0]
    );
    this.addClippingPlane(
      position,
      [0, 1, 0],
      [1, 0, 0],
      new HsToolAction(HsToolActionType.Move, [1], [0, 2], 0, -this.limitMax),
      [0, 0, 1],
      Math.PI
    );
    this.addClippingPlane(
      position,
      [0, -1, 0],
      [1, 0, 0],
      new HsToolAction(HsToolActionType.Move, [1], [0, 2], this.limitMax, 0),
      [0, 0, 1],
      0
    );
    this.addClippingPlane(
      position,
      [0, 0, 1],
      [-1, 0, 0],
      new HsToolAction(HsToolActionType.Move, [2], [0, 1], 0, -this.limitMax),
      [0, 0.7, 0]
    );
    this.addClippingPlane(
      position,
      [0, 0, -1],
      [1, 0, 0],
      new HsToolAction(HsToolActionType.Move, [2], [0, 1], this.limitMax, 0),
      [0, 0.7, 0]
    );
    const boxHelper: HsBoxHelper = new HsBoxHelper(this.graphics, HsBox.makeUnitCube(), HsColor.makeBlack());
    this.lines = boxHelper.makeLines();

    this.axes[0].arrowsY = [this.axes[2].arrow, this.axes[3].arrow];
    this.axes[0].arrowsZ = [this.axes[4].arrow, this.axes[5].arrow];
    this.axes[1].arrowsY = [this.axes[2].arrow, this.axes[3].arrow];
    this.axes[1].arrowsZ = [this.axes[4].arrow, this.axes[5].arrow];

    this.axes[2].arrowsX = [this.axes[0].arrow, this.axes[1].arrow];
    this.axes[2].arrowsZ = [this.axes[4].arrow, this.axes[5].arrow];
    this.axes[3].arrowsX = [this.axes[0].arrow, this.axes[1].arrow];
    this.axes[3].arrowsZ = [this.axes[4].arrow, this.axes[5].arrow];

    this.axes[4].arrowsX = [this.axes[0].arrow, this.axes[1].arrow];
    this.axes[4].arrowsY = [this.axes[2].arrow, this.axes[3].arrow];
    this.axes[5].arrowsX = [this.axes[0].arrow, this.axes[1].arrow];
    this.axes[5].arrowsY = [this.axes[2].arrow, this.axes[3].arrow];

    this.axes.forEach((axis: HsClippingArrow) => {
      this.queue.addObjects(axis.arrow.getArrowsObjects());
      this.queue.addObjects(axis.arrow.getPlanesObjects());
      if (this.lines !== null) {
        this.queue.addObject(this.lines);
      }
      axis.arrow.getArrowsObjects().forEach((arrow: HsGraphicObject) => {
        this.handleArrowsMovement(arrow, true, [0, 0], [1, 1]);
      });
    });
  }

  public createTransformTool(originalPosition: HsHypersceneTypes.Array3): void {
    this.transformAxis = new HsMoveTool(this.graphics, this.queue);
    this.transformAxis.addArrow([0, 0, 1], new HsToolAction(HsToolActionType.Move, [0], [1, 2]), new HsColor(1, 0, 0));
    this.transformAxis.addArrow([0, 0, -1], new HsToolAction(HsToolActionType.Move, [0], [1, 2]), new HsColor(1, 0, 0));
    this.transformAxis.addArrow(
      [0, 1, 0],
      new HsToolAction(HsToolActionType.Move, [1], [0, 2]),
      new HsColor(0, 0, 1),
      Math.PI
    );
    this.transformAxis.addArrow(
      [1, 0, 0],
      new HsToolAction(HsToolActionType.Move, [1], [0, 2]),
      new HsColor(0, 0, 1),
      Math.PI
    );
    this.transformAxis.addArrow(
      [1, 0, 0],
      new HsToolAction(HsToolActionType.Move, [2], [0, 1]),
      new HsColor(0, 0.7, 0)
    );
    this.transformAxis.addArrow(
      [-1, 0, 0],
      new HsToolAction(HsToolActionType.Move, [2], [0, 1]),
      new HsColor(0, 0.7, 0)
    );
    this.transformAxis.worldMatrix.setPosition(originalPosition);
    this.transformAxis.updateChildrenWorldMatrix();
    this.queue.addObjects(this.transformAxis.getArrowsObjects());
    this.queue.addObjects(this.transformAxis.getPlanesObjects());
    if (this.lines !== null) {
      this.queue.addObject(this.lines);
    }
    this.transformAxis.getArrowsObjects().forEach((arrow: HsGraphicObject) => {
      this.handleArrowsMovement(arrow, true, [0, 0], [1, 1]);
    });
  }

  public getBoundingBox(): HsBox {
    const left: number = this.axes[0].arrow.worldMatrix.getPositionX();
    const right: number = this.axes[1].arrow.worldMatrix.getPositionX();
    const top: number = this.axes[3].arrow.worldMatrix.getPositionY();
    const bottom: number = this.axes[2].arrow.worldMatrix.getPositionY();
    const front: number = this.axes[5].arrow.worldMatrix.getPositionZ();
    const back: number = this.axes[4].arrow.worldMatrix.getPositionZ();
    return new HsBox(left, bottom, back, right, top, front);
  }

  public getCenter(): HsVector3 {
    const left: number = this.axes[0].arrow.worldMatrix.getPositionX();
    const right: number = this.axes[1].arrow.worldMatrix.getPositionX();
    const top: number = this.axes[3].arrow.worldMatrix.getPositionY();
    const bottom: number = this.axes[2].arrow.worldMatrix.getPositionY();
    const front: number = this.axes[5].arrow.worldMatrix.getPositionZ();
    const back: number = this.axes[4].arrow.worldMatrix.getPositionZ();
    return new HsVector3(left + right, top + bottom, front + back).multiplyByScalar(0.5);
  }

  public handleArrowsMovement(
    raycastObject: HsGraphicObject,
    isMouseButton: boolean,
    mousePosition: HsHypersceneTypes.Array2,
    canvasSize: HsHypersceneTypes.Array2
  ): boolean {
    let canMoveCamera: boolean = true;

    const left: number = this.axes[0].arrow.worldMatrix.getPositionX();
    const right: number = this.axes[1].arrow.worldMatrix.getPositionX();
    const top: number = this.axes[3].arrow.worldMatrix.getPositionY();
    const bottom: number = this.axes[2].arrow.worldMatrix.getPositionY();
    const front: number = this.axes[5].arrow.worldMatrix.getPositionZ();
    const back: number = this.axes[4].arrow.worldMatrix.getPositionZ();

    this.axes[0].action.min = right - this.limitMin;
    this.axes[0].action.max = right - this.limitMax;
    this.axes[1].action.max = left + this.limitMin;
    this.axes[1].action.min = left + this.limitMax;

    this.axes[2].action.min = top - this.limitMin;
    this.axes[2].action.max = top - this.limitMax;
    this.axes[3].action.max = bottom + this.limitMin;
    this.axes[3].action.min = bottom + this.limitMax;

    this.axes[4].action.min = front - this.limitMin;
    this.axes[4].action.max = front - this.limitMax;
    this.axes[5].action.max = back + this.limitMin;
    this.axes[5].action.min = back + this.limitMax;

    const scaleX: number = Math.abs(right - left) + LINES_VISIBLE_OFFSET;
    const scaleY: number = Math.abs(top - bottom) + LINES_VISIBLE_OFFSET;
    const scaleZ: number = Math.abs(front - back) + LINES_VISIBLE_OFFSET;

    this.lines?.worldMatrix.setPosition([left, bottom, back]);
    this.lines?.worldMatrix.resetScale();
    this.lines?.worldMatrix.scale([scaleX, scaleY, scaleZ]);
    this.lines?.updateBoundingSphere();

    if (!isMouseButton) {
      this.movingObjectUuid = null;
      return canMoveCamera;
    }
    canMoveCamera &&= this.moveTransformAxis(raycastObject, mousePosition, canvasSize);
    this.axes.forEach((axisTuple: HsClippingArrow) => {
      const axis: HsMoveTool = axisTuple.arrow;
      axis.getAllTools().forEach((tool: HsArrowTool) => {
        if (raycastObject.uuid === tool.uuid) {
          canMoveCamera = false;
          this.useToolAction(tool, axis, mousePosition, canvasSize);
          const position: HsVector3 = new HsVector3(0, 0, 0).setFromMatrixPosition(axis.worldMatrix.values);
          const direction: HsVector3 = new HsVector3(0, 0, 0).copy(position).normalize().negate();
          let offset: number = position.length();
          if (offset !== 0) offset *= HsVector3.dot(direction, axisTuple.plane.normal);
          axisTuple.plane.offset = offset;
        } else {
          const scale: HsHypersceneTypes.Array3 = [1, 1, 1];
          const plane: HsGraphicObject = axis.getPlanesObjects()[0];
          if (axisTuple.arrowsX !== null) {
            const x0: number = axisTuple.arrowsX[0].worldMatrix.getPositionX();
            const x1: number = axisTuple.arrowsX[1].worldMatrix.getPositionX();
            axis.worldMatrix.setPositionX((x0 + x1) / 2);
            axis.updateChildrenWorldMatrix();
            axis.updateBoundingSpheres();
            const distance: number = Math.abs(x0 - x1) / 2;
            scale[0] = distance;
          }
          if (axisTuple.arrowsY !== null) {
            const y0: number = axisTuple.arrowsY[0].worldMatrix.getPositionY();
            const y1: number = axisTuple.arrowsY[1].worldMatrix.getPositionY();
            axis.worldMatrix.setPositionY((y0 + y1) / 2);
            axis.updateChildrenWorldMatrix();
            axis.updateBoundingSpheres();
            const distance: number = Math.abs(y0 - y1) / 2;
            if (axisTuple.arrowsZ !== null) scale[0] = distance;
            else scale[2] = distance;
          }
          if (axisTuple.arrowsZ !== null) {
            const z0: number = axisTuple.arrowsZ[0].worldMatrix.getPositionZ();
            const z1: number = axisTuple.arrowsZ[1].worldMatrix.getPositionZ();
            axis.worldMatrix.setPositionZ((z0 + z1) / 2);
            axis.updateChildrenWorldMatrix();
            axis.updateBoundingSpheres();
            const distance: number = Math.abs(z0 - z1) / 2;
            scale[2] = distance;
          }
          plane.worldMatrix.resetScale();
          plane.worldMatrix.scale(scale);
          plane.updateLocalMatrix();
        }
      });
    });
    return canMoveCamera;
  }

  public getIfCameraWasUpdated(): boolean {
    const cameraWasUpdatedTemp: boolean = this.cameraWasUpdated;
    this.cameraWasUpdated = false;
    return cameraWasUpdatedTemp;
  }

  public updateTransformPosition(): void {
    if (this.transformAxis === null) {
      return;
    }
    this.transformAxis.worldMatrix.setPosition(this.findClosestCornerToCamera().values);
    this.transformAxis.updateChildrenWorldMatrix();
    const center: HsVector3 = this.getCenter();
    this.transformAxis.getArrowsObjects().forEach((arrow: HsGraphicObject) => {
      const arrowRotation: HsMatrix3 = HsMatrix3.makeIdentityMatrix().setFromMatrix4(arrow.worldMatrix.values);
      const arrowDirection: HsVector3 = HsVector3.makeUnitVectorY();
      arrowDirection.applyMatrix3(arrowRotation.values);
      arrowDirection.normalize();
      arrowDirection.values[1] = -arrowDirection.values[1];
      const arrowToBoxCenterDirection: HsVector3 = HsVector3.makeZeroVector().setFromMatrixPosition(
        arrow.worldMatrix.values
      );
      arrowToBoxCenterDirection.subtract(center).negate();
      arrowToBoxCenterDirection.normalize();
      arrow.visible = HsVector3.dot(arrowDirection, arrowToBoxCenterDirection) > 0;
    });
  }

  public moveBox(position: HsHypersceneTypes.Array3): void {
    this.movedAmount.add(new HsVector3(...position));
    this.axes.forEach((axisTuple: HsClippingArrow) => {
      const axis: HsMoveTool = axisTuple.arrow;
      axis.worldMatrix.addRelativeTransition(position);
      axis.updateChildrenWorldMatrix();
    });
    this.needUpdateAfterMoving = true;
  }

  public updateMovedBox(): boolean {
    if (!this.needUpdateAfterMoving) {
      return false;
    }
    this.needUpdateAfterMoving = false;
    this.axes.forEach((axisTuple: HsClippingArrow) => {
      const axis: HsMoveTool = axisTuple.arrow;
      axis.updateChildrenWorldMatrix();
      axis.updateBoundingSpheres();
      axisTuple.plane.offset -= HsVector3.dot(this.movedAmount, axisTuple.plane.normal);
    });
    this.movedAmount.set(0, 0, 0);
    return true;
  }

  private findClosestCornerToCamera(): HsVector3 {
    const left: number = this.axes[0].arrow.worldMatrix.getPositionX();
    const right: number = this.axes[1].arrow.worldMatrix.getPositionX();
    const top: number = this.axes[3].arrow.worldMatrix.getPositionY();
    const bottom: number = this.axes[2].arrow.worldMatrix.getPositionY();
    const front: number = this.axes[5].arrow.worldMatrix.getPositionZ();
    const back: number = this.axes[4].arrow.worldMatrix.getPositionZ();
    const corners: HsVector3[] = [
      new HsVector3(left, top, back),
      new HsVector3(left, top, front),
      new HsVector3(left, bottom, back),
      new HsVector3(left, bottom, front),
      new HsVector3(right, top, back),
      new HsVector3(right, top, front),
      new HsVector3(right, bottom, back),
      new HsVector3(right, bottom, front),
    ];
    const cameraPosition: HsVector3 = new HsVector3(...this.camera.worldMatrix.getPositionArray());
    let closestDistanceSquared: number = Number.MAX_SAFE_INTEGER;
    let closestIndex: number = 0;
    for (let i: number = 0; i < corners.length; i++) {
      const distanceSquared: number = HsVector3.distanceSquared(cameraPosition, corners[i]);
      if (distanceSquared < closestDistanceSquared) {
        closestDistanceSquared = distanceSquared;
        closestIndex = i;
      }
    }
    return corners[closestIndex];
  }

  private moveTransformAxis(
    raycastObject: HsObject3D,
    mousePosition: HsHypersceneTypes.Array2,
    canvasSize: HsHypersceneTypes.Array2
  ): boolean {
    if (this.transformAxis === null) {
      return true;
    }
    let canMoveCamera: boolean = true;
    const axis: HsMoveTool = this.transformAxis;
    axis.getAllTools().forEach((tool: HsArrowTool) => {
      if (raycastObject.uuid === tool.uuid) {
        canMoveCamera = false;
        this.useToolAction(tool, axis, mousePosition, canvasSize);
      }
    });
    return canMoveCamera;
  }

  private addClippingPlane(
    originalPosition: HsHypersceneTypes.Array3,
    positionOffset: HsHypersceneTypes.Array3,
    rotation: HsHypersceneTypes.Array3,
    axisAction: HsToolAction,
    color: HsHypersceneTypes.Array3,
    arrowAngle: number = HsHypersceneConstants.PI_DIV_2
  ): void {
    const plane: HsPlane = new HsPlane(...positionOffset, this.limitMin);
    const axis: HsMoveTool = new HsMoveTool(this.graphics, this.queue);
    axis.addArrow(rotation, axisAction, new HsColor(...color), arrowAngle);
    axis.addPlane(rotation, axisAction, this.planeColor, this.limitMin, true, arrowAngle);
    axis.worldMatrix.setPosition(
      new HsVector3(...positionOffset).multiplyByScalar(-this.limitMin).add(new HsVector3(...originalPosition)).values
    );
    axis.updateChildrenWorldMatrix();
    this.axes.push(new HsClippingArrow(axis, plane, axisAction));
  }

  private useToolAction(
    tool: HsArrowTool,
    axis: HsMoveTool,
    position: HsHypersceneTypes.Array2,
    canvasSize: HsHypersceneTypes.Array2
  ): void {
    if (tool.uuid !== this.movingObjectUuid) {
      this.movingObjectUuid = tool.uuid;
      axis.needUpdateOrigin();
    }
    const width: number = canvasSize[0];
    const height: number = canvasSize[1];
    const aspect: number = width / height;
    const x: number = position[0] - width;
    const y: number = height - position[1] - height / 2;
    const vector: HsVector3 = new HsVector3(x, y * aspect, 0).unproject(
      this.camera.projectionMatrixInverse.values,
      this.camera.worldMatrix.values
    );
    const direction: HsVector3 = new HsVector3(0, 0, -1);
    const cameraPosition: HsVector3 = new HsVector3(0, 0, 0).setFromMatrixPosition(this.camera.worldMatrix.values);
    direction.applyMatrix3(HsClippingBox.#matrix3Temp1.setFromMatrix4(this.camera.worldMatrixInverse.values).values);
    vector.subtract(cameraPosition);
    vector.multiplyByScalar(1 / (this.camera.near * width));
    vector.add(direction);
    vector.normalize();
    this.cameraWasUpdated = true;
    if (axis === this.transformAxis) {
      this.doTransformAction(tool, axis, vector, cameraPosition);
      return;
    }
    axis.doAction(tool.action, vector, cameraPosition);
  }

  private doTransformAction(tool: HsArrowTool, axis: HsMoveTool, vector: HsVector3, cameraPosition: HsVector3): void {
    const oldPosition: HsVector3 = new HsVector3(...axis.worldMatrix.getPositionArray());
    axis.doAction(tool.action, vector, cameraPosition);
    const newPosition: HsVector3 = new HsVector3(...axis.worldMatrix.getPositionArray());
    newPosition.subtract(oldPosition);
    this.moveBox(newPosition.values);
  }
}
